import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING } from "const"
import {
    StateType,
    deepCopy,
} from "client/utils"
import {
    StoreType,
    TaskProps,
    TaskList,
    AccountUserList,
    ProjectUserList,

    LoadTaskById,
    LoadTaskAttachments,
    LoadAccountUsers,
    LoadProjectUsers,
    LoadProjectUserGroups,
    LoadProjectPermissions,
    GetTaskAccess,

    setTaskList,
    setTaskAttachmentsView,
    setAccountUserList,
    setProjectUserList,
    setSystemProjectUserGroups,
    setSystemProjectPermissions,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    FireIcon,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import NotFound from "client/parts/notFound"
import Form from "./form"
import SubtaskList from "../list"
import TaskComments from "./comments"
import State from "./state"

import {
    HighPriorityWrapper,
    TaskLayout,
    TaskBodyColumn,
} from "./styled"

// "View" page can be opened from main task list and from "Subtask list".
// So, several "View" pages can be opened at the same time. And current "taskView" cannot be stored on Redux Store level
const View = ({
    taskId = null,
}: TaskProps) => {
    const taskList = useSelector((state: StoreType) => state.task.taskList)
    const taskParticipantList = useSelector((state: StoreType) => state.taskParticipant.taskParticipantList)
    const taskObserverList = useSelector((state: StoreType) => state.taskObserver.taskObserverList)
    const task = taskList[taskId] || null

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        // all tasks, participants, observers - these lists are loaded on subtask list level
        const promiseArray: Promise<any>[] = [
            LoadTaskById(taskId, userData.accountId).then((resTask) => {
                const taskListCopy: TaskList = deepCopy(taskList)
                taskListCopy[taskId] = resTask
                dispatch(setTaskList(taskListCopy))

                if (!project.id && resTask && resTask.projectId) {
                    return Promise.all([
                        // to display user status for project level tasks
                        LoadProjectUsers(resTask.projectId).then((resUsers) => {
                            const newProjectUserList: ProjectUserList = { [resTask.projectId]: resUsers }
                            dispatch(setProjectUserList(newProjectUserList))
                        }),
                        // to check user access for project level tasks
                        LoadProjectUserGroups(userData.userId, userData.accountId, resTask.projectId)
                        .then((userGroups) => {
                            dispatch(setSystemProjectUserGroups(resTask.projectId, userGroups))
                        }),
                        LoadProjectPermissions(userData.accountId, resTask.projectId)
                        .then((permissions) => {
                            dispatch(setSystemProjectPermissions(resTask.projectId, permissions.permissions))
                        }),
                    ])
                }
                return null
            }),
            LoadTaskAttachments(taskId).then((res) => {
                dispatch(setTaskAttachmentsView({ [taskId]: res }))
            }),
        ]

        if (!project.id) {
            promiseArray.push(
                // to assign users on account level
                LoadAccountUsers(userData.accountId).then((res) => {
                    const newAccountUserList: AccountUserList = { [userData.accountId]: res }
                    dispatch(setAccountUserList(newAccountUserList))
                }),
            )
        }

        setLoadState(PENDING)
        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
        })
    }, [])

    const { allowedToView } = GetTaskAccess(task, taskParticipantList, taskObserverList, userData)

    if (!allowedToView) return <AccessDenied />
    if (!task) return <NotFound />

    return (
        <Container>
            <PageHeader>
                <PageTitle>
                    {`${lang.task} # ${task.id || ""} - ${task.name || ""}`}
                </PageTitle>
                <HighPriorityWrapper active={task.highPriority}>
                    {lang.highPriority}
                    <FireIcon active={task.highPriority} />
                </HighPriorityWrapper>
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <TaskLayout>
                    <TaskBodyColumn>
                        <Form task={task} />
                        <SubtaskList parentTaskId={taskId} />
                        <TaskComments task={task} />
                    </TaskBodyColumn>

                    <State task={task} />
                </TaskLayout>
            </PageContent>
        </Container>
    )
}
export default View
